import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { tapResponse } from "@ngrx/operators";
import { getState, patchState, WritableStateSource } from "@ngrx/signals";
import { rxMethod, RxMethod } from "@ngrx/signals/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { ConfigService } from "@ph-core/services/config/config.service";
import { ContractSaveFacade } from "@ph-core/services/contract-save.facade";
import { PhDialog } from "@ph-core/services/dialog/dialog.service";
import { CarfaxResponse } from "@ph-model/api/response/carfax-response.model";
import { DecodeVinResponse } from "@ph-model/api/response/decode-vin.response";
import { CheckoutFormValues } from "@ph-model/checkout/checkout-form-values.model";
import { SubmittedCartItem } from "@ph-model/checkout/submit_cart/response/submitted-cart-item.model";
import { SubmitCartRequest } from "@ph-model/request/submit-cart.request";
import { RemitStoreType } from "@ph-remit/store/remit/remit.store";
import { PhNotificationComponent } from "@ph-shared/components/notification/notification/notification.component";
import { PhNotificationType } from "@ph-shared/components/notification/notification/notification.interface";
import { ControlDateFormatter } from "@ph-shared/utils/date-control-formater";
import { CartItem } from "@ph-shopping/model/cart";
import { SubmitCartModel } from "@ph-shopping/model/cart/submit-cart.model";
import { VehicleStoreType } from "@ph-shopping/store/vehicle/vehicle.store";
import { UserStoreType } from "@ph-store/user/user.store";
import { catchError, filter, map, Observable, of, pipe, switchMap, tap } from "rxjs";
import { SubmitCartSuccess } from "../cart-store.types";

export interface CartStoreForSubmitMethods extends WritableStateSource<object> {
    submittedCart(): SubmittedCartItem[];
    cartTotal(): number;
    checkoutFormValues(): CheckoutFormValues;
}

export interface CartStoreSubmitDeps {
    userStore: UserStoreType;
    vehicleStore: VehicleStoreType;
    dialog: PhDialog;
    translate: TranslateService;
    router: Router;
    remitStore: RemitStoreType;
    httpClient: HttpClient;
    contractSaveFacade: ContractSaveFacade;
}

export interface CartStoreSubmitMethodsReturn {
    submitCart: RxMethod<SubmitCartModel | SubmitCartRequest>;
}

export function withCartStoreSubmitMethods(
    store: CartStoreForSubmitMethods,
    deps: CartStoreSubmitDeps
): CartStoreSubmitMethodsReturn {
    const environment = ConfigService.getEnvironment();
    const {
        userStore,
        vehicleStore,
        dialog,
        translate,
        router,
        remitStore,
        httpClient,
        contractSaveFacade,
    } = deps;

    const submitCart = rxMethod<SubmitCartModel | SubmitCartRequest>(
        pipe(
            switchMap((payload: SubmitCartModel | SubmitCartRequest) => {
                const isdRequired =
                    'cart' in payload
                        ? payload.cart.filter((item) =>
                            environment.features.shopping.productTypesRequiringISD.includes(item.product.productType)
                        ).length > 0
                        : false;

                return isdRequired && !vehicleStore.values().inServiceDate
                    ? calculateInServiceDateFromVin(vehicleStore.values().vin!).pipe(
                        tap(
                            (inServiceDate: string | null) =>
                                inServiceDate &&
                                vehicleStore.updateProductForm({ inServiceDate: ControlDateFormatter.encode(inServiceDate) })
                        ),
                        map(() => payload)
                    )
                    : of(payload);
            }),
            switchMap((payload: SubmitCartModel | SubmitCartRequest) => {
                const user = userStore.user();
                if (!user) {
                    console.error('User not found. Getting checkout form aborted.');
                    return of([]);
                }
                return contractSaveFacade.submitCart(payload, getState(vehicleStore), user).pipe(
                    tapResponse<{ invalidCart: CartItem[]; submittedCart: SubmittedCartItem[] }>({
                        next: ({
                            invalidCart,
                            submittedCart,
                        }: {
                            invalidCart: CartItem[];
                            submittedCart: SubmittedCartItem[];
                        }) => {
                            patchState(store, {
                                cart: invalidCart,
                                validatedCart: invalidCart,
                                submittedCart: [...store.submittedCart(), ...submittedCart],
                                savedQuote: false,
                                submittedCartSuccess: submittedCart.length > 0,
                                checkoutCartContainsError: invalidCart.length > 0,
                            });
                        },
                        error: (error: unknown) => {
                            const errorMessage =
                                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                                    ? error.error
                                    : String(error)) || '';
                            console.error(errorMessage);

                            patchState(store, { error: errorMessage, success: false, savedQuote: false });
                        },
                    }),
                    switchMap(({ submittedCart }: SubmitCartSuccess) => {
                        if (!submittedCart.length) {
                            return of(null);
                        }

                        return dialog
                            .open(PhNotificationComponent, {
                                data: {
                                    type: PhNotificationType.approved,
                                    actions: [
                                        {
                                            text: translate.instant('insuranceContractSavedPopup.continue'),
                                            act: (ref): void => {
                                                ref.close(true);
                                                void router.navigate(['/home']);
                                            },
                                        },
                                        {
                                            text: translate.instant('insuranceContractSavedPopup.close'),
                                            act: (ref) => ref.close(),
                                        },
                                    ],
                                    value: {
                                        title: translate.instant('insuranceContractSavedPopup.title'),
                                        message: translate.instant('insuranceContractSavedPopup.body'),
                                    },
                                },
                            })
                            .afterClosed()
                            .pipe(
                                tap((val) => {
                                    remitStore.clearPendingContracts();
                                    if (!val) {
                                        remitStore.getPendingContracts();
                                    }
                                })
                            );
                    })
                );
            })
        )
    );

    const calculateInServiceDateFromVin = (vin?: string): Observable<string | null> => {
        const user = userStore.user();
        if (!user || !vin) {
            return of(null);
        }
        const path = environment.features.shopping.supportClassicVinDecodeApi
            ? `${environment.apiUrl}/vindecode`
            : `${environment.apiUrl}/vindecode/v2`;

        const cmsDealerNumber = user.cmsDealerNumber;

        return httpClient.get<DecodeVinResponse>(path, { params: { vin, dealer: cmsDealerNumber } }).pipe(
            catchError(() => {
                return of<DecodeVinResponse | null>(null);
            }),
            filter((value): value is DecodeVinResponse => value !== null),
            switchMap((decodeVinResponse: DecodeVinResponse) => {
                if (!environment.features.shopping.supportCarfaxDecoding || decodeVinResponse.inserviceDate) {
                    return of(decodeVinResponse.inserviceDate);
                } else {
                    type CarfaxApiResponse = {
                        standardResponse: { statusCode: number; responseData: CarfaxResponse };
                    };
                    return httpClient
                        .get<CarfaxApiResponse>(`${environment.apiUrl}/carfax`, {
                            params: { vin: vin ?? '' },
                        })
                        .pipe(
                            catchError((): Observable<CarfaxApiResponse> => {
                                return of({
                                    standardResponse: {
                                        statusCode: 404,
                                        responseData: {} as CarfaxResponse,
                                    },
                                });
                            }),
                            map(({ standardResponse: { statusCode, responseData: result } }: CarfaxApiResponse) =>
                                statusCode === 200 ? result : null
                            ),
                            map((carfaxResponse) => {
                                if (carfaxResponse) {
                                    return carfaxResponse.inServiceDate;
                                } else {
                                    return decodeVinResponse.inserviceDate;
                                }
                            })
                        );
                }
            })
        );
    };

    return {
        submitCart,

    }
}
